/**
 * «Exportar relatório» — quando o botão existe, e como ele está (set/2026).
 *
 * O painel «Comentários» ganhou um botão que baixa o PDF da aprovação: a ficha
 * do link, cada página da arte com as anotações numeradas e, embaixo de cada
 * arte, a lista delas. Do plano **Pro** para cima.
 *
 * Lógica fora do `.tsx` pelo motivo de sempre: `node --test` é o único caminho
 * de teste do projeto, e o que precisa de teste é justamente a régua — três
 * estados (existe, travado, não existe) que cruzam a chave do link, o plano e
 * a presença de credencial.
 */
import type { PlanName, PublicViewTools } from "./api";
import type { ToolLocks } from "./viewerTools";

export interface RelatorioDoPainel {
  /** Falso quando o botão aparece opaco com o selo do plano. */
  disponivel: boolean;
  /** O plano em que ele existe, quando está travado. Nulo quando disponível. */
  plano: PlanName | null;
}

/**
 * O estado do botão, ou `null` quando ele nem é desenhado.
 *
 * Três entradas, nesta ordem:
 *
 * 1. **sem arquivo** — não há o que relatar;
 * 2. **travado pelo plano** (`toolLocks` já leu o `tool_access`) — desenha
 *    opaco, com selo; é o convite que o link Starter recebe, e a rota
 *    responde 403;
 * 3. **`tools.notes_report`** — a régua já fundida: ela é `false` no link que
 *    desligou a chave, no anônimo sem sessão e no link de visualização
 *    exportada, onde a rota não existe.
 */
export function relatorioDoPainel(args: {
  assetId: string | null;
  tools: Pick<PublicViewTools, "notes_report">;
  /** O que `toolLocks(tool_access)` devolveu. Vazio fora do link público. */
  locks?: ToolLocks | null;
}): RelatorioDoPainel | null {
  const { assetId, tools, locks } = args;
  if (!assetId) return null;
  const plano = locks?.notes_report;
  if (plano) return { disponivel: false, plano };
  if (!tools.notes_report) return null;
  return { disponivel: true, plano: null };
}

/**
 * O 409 que não é erro: páginas do documento a caminho (set/2026).
 *
 * O viewer de documento rasteriza sob demanda, e página anotada que ninguém
 * abriu — ou que o GC despejou — ainda não tem arte. O servidor pede ao worker
 * as que faltam e responde 409 com este `code`; a tela repete o pedido sozinha,
 * mostrando o andamento, até o PDF chegar ou o prazo acabar.
 */
export const CODIGO_PAGINAS_EM_PREPARO = "notes_report_pages_preparing";

export interface PaginasEmPreparo {
  /** Quantas páginas do relatório ainda estão sem arte. */
  faltando: number;
  /** Quantas páginas o relatório vai trazer. */
  total: number;
}

/** O corpo do 409, quando é a espera, ou `null` quando é erro de verdade. */
export function paginasEmPreparo(corpo: string): PaginasEmPreparo | null {
  try {
    const body = JSON.parse(corpo) as { detail?: unknown };
    const detail = body?.detail as Record<string, unknown> | null | undefined;
    if (!detail || typeof detail !== "object" || detail.code !== CODIGO_PAGINAS_EM_PREPARO) {
      return null;
    }
    const faltando = Number(detail.pending);
    const total = Number(detail.total);
    if (!Number.isFinite(faltando) || !Number.isFinite(total)) return null;
    return { faltando: Math.max(0, faltando), total: Math.max(0, total) };
  } catch {
    return null;
  }
}

/**
 * Quanto esperar antes da tentativa `n` (1 = a primeira repetição).
 *
 * Uma página de documento leva segundos na fila barata; começar em 2 s e ir
 * afrouxando até 5 s poupa a fila — cada pedido reenfileira o que falta.
 */
export function esperaDaTentativa(n: number): number {
  return Math.min(5_000, 1_000 + 1_000 * Math.max(1, n));
}

/**
 * Até quando repetir. Passado isto a tela desiste e mostra a mensagem do
 * servidor («Preparando as páginas… tente de novo em instantes»): a fila pode
 * estar tomada, e esperar em silêncio para sempre é pior do que dizer.
 */
export const PRAZO_DA_ESPERA_MS = 120_000;

/**
 * Pede o relatório até ele deixar de ser «páginas em preparo».
 *
 * Devolve a primeira resposta que não for a espera — o PDF, ou o erro que a
 * tela vai mostrar — e, passado o prazo, a própria espera, cuja mensagem diz
 * «tente de novo em instantes». `clone` porque o corpo da resposta devolvida
 * ainda tem de chegar inteiro a quem a salva ou monta a mensagem de erro.
 */
export async function pedirComEspera(
  pedir: () => Promise<Response>,
  opcoes: {
    onPreparing?: (preparo: PaginasEmPreparo) => void;
    esperar?: (ms: number) => Promise<void>;
    agora?: () => number;
  } = {},
): Promise<Response> {
  const esperar =
    opcoes.esperar ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const agora = opcoes.agora ?? Date.now;
  const inicio = agora();
  for (let tentativa = 1; ; tentativa += 1) {
    const res = await pedir();
    if (res.status !== 409 || agora() - inicio >= PRAZO_DA_ESPERA_MS) return res;
    const preparo = paginasEmPreparo(await res.clone().text().catch(() => ""));
    if (!preparo) return res;
    opcoes.onPreparing?.(preparo);
    await esperar(esperaDaTentativa(tentativa));
  }
}

/** O texto do botão enquanto ele trabalha. */
export function rotuloDoRelatorio(preparo: PaginasEmPreparo | null): string {
  if (!preparo || preparo.total <= 0) return "Gerando relatório...";
  const prontas = Math.min(preparo.total, Math.max(0, preparo.total - preparo.faltando));
  return `Preparando páginas (${prontas} de ${preparo.total})...`;
}
